import React from 'react';
import { FaLightbulb, FaGlobe, FaShieldAlt, FaSearch, FaMobileAlt, FaRegClock, FaRegCopyright } from 'react-icons/fa';

const tips = [
  {
    icon: <FaSearch className="text-blue-500 text-2xl" />,
    title: 'Make It Memorable & Brandable',
    desc: 'Choose a name that is easy to remember, spell, and pronounce. Avoid numbers and hyphens that can cause confusion.'
  },
  {
    icon: <FaGlobe className="text-green-500 text-2xl" />,
    title: 'Choose the Right TLD',
    desc: '.com is the most recognized, but consider other TLDs like .io for tech, .ai for AI projects, or country-specific ones like .uk or .de.'
  },
  {
    icon: <FaRegCopyright className="text-purple-500 text-2xl" />,
    title: 'Check Trademark Availability',
    desc: 'Ensure your domain doesn\'t infringe on existing trademarks to avoid legal issues. Search the USPTO database for existing trademarks.'
  },
  {
    icon: <FaMobileAlt className="text-yellow-500 text-2xl" />,
    title: 'Mobile-Friendly & Social Media Ready',
    desc: 'Check if the name is available on major social platforms. Shorter names work better for mobile displays and social media handles.'
  },
  {
    icon: <FaRegClock className="text-red-500 text-2xl" />,
    title: 'Consider Long-Term Use',
    desc: 'Choose a name that will grow with your business. Avoid trends or names that might become outdated.'
  },
  {
    icon: <FaShieldAlt className="text-indigo-500 text-2xl" />,
    title: 'Protect Your Brand',
    desc: 'Consider registering common misspellings and different TLDs to protect your brand and prevent cybersquatting.'
  }
];

const WhyItMattersSection: React.FC = () => (
  <section className="max-w-5xl mx-auto mt-16 mb-8 px-4 py-8 bg-white rounded-xl shadow-md border border-gray-100 sm:mx-4">
    <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-center text-blue-700">How to choose the perfect domain name</h2>
    <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
      Your domain name is your digital address. Follow these expert tips to select a name that strengthens your brand and drives traffic.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 px-2 sm:px-0">
      {tips.map((tip, idx) => (
        <div 
          key={idx} 
          className="flex flex-col items-start p-5 bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100"
        >
          <div className="p-2 mb-3 rounded-lg bg-white shadow-sm">
            {tip.icon}
          </div>
          <h3 className="font-bold text-lg mb-2 text-gray-800">{tip.title}</h3>
          <p className="text-gray-600 text-sm leading-relaxed">{tip.desc}</p>
        </div>
      ))}
    </div>
    <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
      <div className="flex items-start">
        <FaLightbulb className="text-yellow-500 text-xl mt-1 mr-3 flex-shrink-0" />
        <div>
          <h4 className="font-bold text-blue-800 mb-1">Pro Tip</h4>
          <p className="text-sm text-gray-700">
            Use our domain checker to test different name variations and secure your domain before someone else does. 
            Consider purchasing multiple TLDs to protect your brand across different regions and platforms.
          </p>
        </div>
      </div>
    </div>
  </section>
);

export default WhyItMattersSection;
